'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';

/**
 * Performance optimization component for addressing Core Web Vitals issues
 * Includes image optimization, lazy loading, and resource hints
 */
export function PerformanceOptimizations() {
  useEffect(() => {
    optimizeImageLoading();
    optimizeCumulativeLayoutShift();
  }, []);

  return null; // This component handles performance optimizations behind the scenes
}

/**
 * Optimize image loading to reduce CLS and improve LCP
 */
function optimizeImageLoading() {
  // Add loading="lazy" to all images except above-the-fold
  const images = document.querySelectorAll('img');
  images.forEach((img, index) => {
    // First 3 images are likely above the fold
    if (index > 2 && !img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
    }

    // Add decode="async" for better performance
    img.setAttribute('decoding', 'async');
  });
}

/**
 * Optimize Cumulative Layout Shift (CLS)
 */
function optimizeCumulativeLayoutShift() {
  // Reserve layout space for images without explicit dimensions.
  const images = document.querySelectorAll<HTMLImageElement>('img:not([width]):not([height])');
  images.forEach((img) => {
    // A non-default object-fit means the author already sized this image's box
    // (avatars, logos, cover art). Forcing 16/9 onto those distorts them.
    if (getComputedStyle(img).objectFit !== 'fill') return;

    // Prevent layout shift by setting dimensions
    img.style.aspectRatio = '16/9'; // Default aspect ratio
    img.style.width = '100%';
    img.style.height = 'auto';
  });

  // next/font handles font loading; avoid extra main-thread work here.
}

/**
 * Optimized image component with performance enhancements
 */
interface OptimizedImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  className?: string;
  sizes?: string;
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  priority = false,
  placeholder = 'blur',
  blurDataURL,
  className,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
}: OptimizedImageProps) {
  // Generate blur placeholder if not provided
  const defaultBlurDataURL = generateBlurDataURL(width, height);

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      priority={priority}
      placeholder={placeholder}
      blurDataURL={blurDataURL || defaultBlurDataURL}
      className={className}
      sizes={sizes}
      quality={85} // Optimized quality for performance
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      style={{
        // Prevent layout shift
        width: '100%',
        height: 'auto',
      }}
    />
  );
}

/**
 * Generate a blur data URL for placeholder
 */
function generateBlurDataURL(width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Create a gradient blur effect
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#f3f4f6');
  gradient.addColorStop(1, '#e5e7eb');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return canvas.toDataURL();
}

/**
 * Resource hints component for performance optimization
 */
export function ResourceHints() {
  useEffect(() => {
    // DNS prefetch for external domains
    const dnsPrefetchDomains = [
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'cdn.discordapp.com',
      'discord.com',
      'www.google-analytics.com',
    ];

    dnsPrefetchDomains.forEach((domain) => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });

    // Preconnect to critical origins
    const preconnectOrigins = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'];

    preconnectOrigins.forEach((origin) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      if (origin.includes('gstatic')) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    });
  }, []);

  return null;
}

/**
 * Critical CSS inlining for above-the-fold content
 */
export function CriticalCSS() {
  const criticalStyles = `
    /* Critical styles for above-the-fold content */
    .hero-section {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .navigation {
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 1000;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
    }
    
    /* Font display optimization */
    @font-face {
      font-family: 'Inter';
      font-display: swap;
    }
    
    /* Loading states to prevent CLS */
    .loading-skeleton {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
    }
    
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: criticalStyles,
      }}
    />
  );
}

/**
 * Service Worker registration for caching optimization
 */
export function ServiceWorkerRegistration() {
  useEffect(() => {
    if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') {
      return;
    }

    const register = () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    };

    if ('requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(register);
      return () => window.cancelIdleCallback(idleCallback);
    }

    const timeout = globalThis.setTimeout(register, 2000);
    return () => globalThis.clearTimeout(timeout);
  }, []);

  return null;
}

/**
 * Lazy loading component for non-critical resources
 */
interface LazyLoadProps {
  children: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
}

export function LazyLoad({ children, rootMargin = '100px', threshold = 0.1 }: LazyLoadProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [elementRef, setElementRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!elementRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold,
      },
    );

    observer.observe(elementRef);

    return () => observer.disconnect();
  }, [elementRef, rootMargin, threshold]);

  return (
    <div ref={setElementRef}>
      {isVisible ? children : <div className="loading-skeleton h-32" />}
    </div>
  );
}
